import { createFeatureSelector, createSelector } from '@ngrx/store';
import { getCourseExperienceProductXid, getCourseExperienceData } from '@mhe/ol-platform';

/**
 * @publicApi
 * @description Selector used to obtain information about the application context.
 * @example
 * this.store.pipe(
 *  select(getApplicationContext),
 *  first(),
 *  tap( context => console.log('got current shell context!', context))
 * );
 */
const getApplicationContext = createFeatureSelector('context');
const ɵ0 = (state) => state.application;
/**
 * @publicApi
 * @description Selector used to obtain application name.
 * @example
 * this.store.pipe(
 *  select(getApplicationName),
 *  first(),
 *  tap( app => console.log('got application context.application property!', app))
 * );
 */
const getApplicationName = createSelector(getApplicationContext, ɵ0);
const ɵ1 = (state) => state.sectionXid;
const getApplicationSectionXid = createSelector(getApplicationContext, ɵ1);
const ɵ2 = (state) => state.selectedOrg;
const getSelectedOrg = createSelector(getApplicationContext, ɵ2);
const ɵ3 = (state) => state.selectedRoles;
const getSelectedRoles = createSelector(getApplicationContext, ɵ3);

// Using `any` for now.
// Shell slice contains the roleGroup value.  Think about rehome or adding Shell state model
const getShellContext = createFeatureSelector('shell');
/**
 * @publicApi
 * @description Builds a (base) route from platform metadata.
 * @param route Allows for segments of the route to be overridden
 * @example
 * this.store.pipe(
 *  select(getRoute({ feature: 'dashboard' })),
 *  first(),
 *  tap( route => console.log('got route to dashboard!', route))
 * );
 */
const getRoute = (route) => createSelector(getApplicationContext, getShellContext, (appContext, shellContext) => {
    const roleGroup = (route && route.roleGroup) || shellContext.roleGroup;
    const orgXid = (route && route.orgXid) || appContext.selectedOrgXid;
    const feature = (route && route.feature) || null;
    // Allows for an explicit null value to be passed to handle cases where
    // we wish to reset this value or the feature does not expect a sectionXid
    const sectionXid = route && typeof route['sectionXid'] !== 'undefined' && route.sectionXid === null
        ? null
        : (route && route.sectionXid) || appContext.sectionXid;
    return ['', roleGroup, orgXid, sectionXid, feature].filter((segment) => segment !== null).join('/');
});

const ɵ0$1 = (state) => state.user;
/**
 * @publicApi
 * @description Selector used to obtain information about the user.
 * @example
 * this.store.pipe(
 *  select(getPerson),
 *  first(),
 *  tap( person => console.log('got current user info!', person))
 * );
 */
const getPerson = createSelector(getApplicationContext, ɵ0$1);

const getState = createFeatureSelector('sections');
const ɵ0$2 = (state) => state;
const getSections = createSelector(getState, ɵ0$2);
const getSectionByXid = (xid) => createSelector(getState, (state) => state && state[xid]);
const getSectionConfigByXid = (xid) => createSelector(getSectionByXid(xid), (section) => section && section.config);

const getState$1 = createFeatureSelector('products');
const ɵ0$3 = (state) => state;
const getProducts = createSelector(getState$1, ɵ0$3);
const getProductByXid = (xid) => createSelector(getState$1, (state) => state && state[xid]);

const getSession = createFeatureSelector('session');
const ɵ0$4 = (state) => state.token;
const getUserToken = createSelector(getSession, ɵ0$4);
const ɵ1$1 = (state) => !!state.emulatorXid;
const isEmulating = createSelector(getSession, ɵ1$1);

const LMI_ERROR_CODE_SECTION_EXPIRED = 'LM-S-01';
const LMI_ERROR_CODE_SECTION_ARCHIVED = 'LM-S-03';
const LMI_ERROR_CODE_LICENSE_EXPIRED = 'LM-ENT-01';
const ɵ0$5 = (sections, xid) => sections[xid];
/** get current (xid) values */
const getSelectedSection = createSelector(getSections, getApplicationSectionXid, ɵ0$5);
const ɵ1$2 = (section) => {
    return section && section.resources && section.resources.length && section.resources[0].product_xid;
};
const getSelectedProductXid = createSelector(getSelectedSection, ɵ1$2);
const ɵ2$1 = (section) => {
    return section && section.resources && section.resources.length && section.resources[0].platform;
};
const getSelectedSectionPlatform = createSelector(getSelectedSection, ɵ2$1);
const ɵ3$1 = (section) => {
    var _a;
    if ((_a = section === null || section === void 0 ? void 0 : section.resources) === null || _a === void 0 ? void 0 : _a.length) {
        for (const resource of section.resources) {
            if (resource && resource.platform === 'openlearning') {
                return {
                    platform: resource.platform,
                    resourceId: resource.resource_id,
                };
            }
        }
        if (isEmulating) {
            for (const resource of section.resources) {
                if (resource && (resource.platform === 'aleks' || resource.platform === 'redbird')) {
                    return {
                        platform: resource.platform,
                        resourceId: resource.resource_id,
                    };
                }
            }
        }
    }
    return undefined;
};
const getSelectedProductResource = createSelector(getSelectedSection, isEmulating, ɵ3$1);
const ɵ4 = (productXid, products) => {
    return productXid && products && products[productXid];
};
const getSelectedProduct = createSelector(getSelectedProductXid, getProducts, ɵ4);
const ɵ5 = (selectedProduct, productResource, allProducts) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    if (selectedProduct) {
        return (_d = (_c = (_b = (_a = selectedProduct === null || selectedProduct === void 0 ? void 0 : selectedProduct.config) === null || _a === void 0 ? void 0 : _a.configData) === null || _b === void 0 ? void 0 : _b.nwea) === null || _c === void 0 ? void 0 : _c.linking_enabled) !== null && _d !== void 0 ? _d : false;
    }
    if (productResource && allProducts) {
        const products = Object.values(allProducts);
        const found = products.find((f) => f.sourceName === productResource.platform && f.sourceId === productResource.resourceId);
        return (_h = (_g = (_f = (_e = found === null || found === void 0 ? void 0 : found.config) === null || _e === void 0 ? void 0 : _e.configData) === null || _f === void 0 ? void 0 : _f.nwea) === null || _g === void 0 ? void 0 : _g.linking_enabled) !== null && _h !== void 0 ? _h : false;
    }
    return false;
};
/** specifically design for checking the external system (NWEA) product config linking */
const hasNweaEnabledForProduct = createSelector(getSelectedProduct, getSelectedProductResource, getProducts, ɵ5);
const ɵ6 = (section) => { var _a, _b; return (_b = (_a = section === null || section === void 0 ? void 0 : section.section_issues) === null || _a === void 0 ? void 0 : _a.section_code) === null || _b === void 0 ? void 0 : _b.includes(LMI_ERROR_CODE_SECTION_EXPIRED); };
const isSectionExpired = createSelector(getSelectedSection, ɵ6);
const ɵ7 = (section) => { var _a, _b; return (_b = (_a = section === null || section === void 0 ? void 0 : section.section_issues) === null || _a === void 0 ? void 0 : _a.section_code) === null || _b === void 0 ? void 0 : _b.includes(LMI_ERROR_CODE_SECTION_ARCHIVED); };
const isSectionArchived = createSelector(getSelectedSection, ɵ7);
const ɵ8 = (section) => { var _a, _b; return (_b = (_a = section === null || section === void 0 ? void 0 : section.section_issues) === null || _a === void 0 ? void 0 : _a.entitlement_code) === null || _b === void 0 ? void 0 : _b.includes(LMI_ERROR_CODE_LICENSE_EXPIRED); };
const hasCourseLicenseExpired = createSelector(getSelectedSection, ɵ8);
const ɵ9 = (sections, xid) => sections[xid];
// TODO: REMOVE
/** get current (xid) values */
const getCurrentApplicationContextSection = createSelector(getSections, getApplicationSectionXid, ɵ9);
const ɵ10 = (products, xid) => products[xid];
const getCurrentCourseExperienceProduct = createSelector(getProducts, getCourseExperienceProductXid, ɵ10);

const getLandingPageState = createFeatureSelector('landingPage');
const ɵ0$6 = (landingPage, roles, org) => {
    var _a, _b;
    const role = roles[0];
    const productResourceSections = (_b = (_a = landingPage === null || landingPage === void 0 ? void 0 : landingPage[role]) === null || _a === void 0 ? void 0 : _a[org === null || org === void 0 ? void 0 : org.xid]) === null || _b === void 0 ? void 0 : _b.sections;
    return productResourceSections !== null && productResourceSections !== void 0 ? productResourceSections : null;
};
const getProductResourceSections = createSelector(getLandingPageState, getSelectedRoles, getSelectedOrg, getSelectedSection, ɵ0$6);

const getExternalSystems = createFeatureSelector('externalSystems');
const getExternalSystemsByOrgXid = (xid) => createSelector(getExternalSystems, (systems) => systems[xid] || []);

const getEnvironmentContextState = createFeatureSelector('environmentContext');
const ɵ0$7 = (state, props) => {
    const stateMap = Object.assign({}, state);
    return stateMap[props.key];
};
/**
 * @publicApi
 */
const getUrlBy = createSelector(getEnvironmentContextState, ɵ0$7);

const ɵ0$8 = (courseExperience, context, section) => {
    return {
        context_id: section.resources[0].resource_id,
        context_title: courseExperience.courseName,
        custom_asset_id: courseExperience.courseId,
        custom_grades: section.grade_levels[0],
        custom_product_id: courseExperience.productXID,
        custom_section_id: context.sectionXid,
        custom_organization_id: context.selectedOrgXid,
        custom_section_title: section.name,
        provider: section.resources[0].platform,
        custom_selected_role: context.selectedRoles[0],
    };
};
const getLtiLaunchData = createSelector(getCourseExperienceData, getApplicationContext, getSelectedSection, ɵ0$8);

/**
 * This library hosts selectors and models that are intended
 * to be used by integrating feature modules.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { getApplicationContext, getApplicationName, getApplicationSectionXid, getCurrentApplicationContextSection, getCurrentCourseExperienceProduct, getExternalSystems, getExternalSystemsByOrgXid, getLtiLaunchData, getPerson, getProductByXid, getProductResourceSections, getProducts, getRoute, getSectionByXid, getSectionConfigByXid, getSections, getSelectedOrg, getSelectedProduct, getSelectedProductResource, getSelectedProductXid, getSelectedRoles, getSelectedSection, getSelectedSectionPlatform, getUrlBy, getUserToken, hasCourseLicenseExpired, hasNweaEnabledForProduct, isEmulating, isSectionArchived, isSectionExpired, getState as ɵa, getState$1 as ɵb, getEnvironmentContextState as ɵc };
